import { Progress } from '@/components/ui/progress'

interface JobProgressBarProps {
  progressPct: number
  periodsCompleted: number
  totalPeriods: number
  periodsFailed: number
  periodStart?: string | null
  periodEnd?: string | null
  className?: string
}

/**
 * Progress bar component showing job completion status.
 *
 * Features:
 * - Visual progress bar with percentage
 * - Breakdown text showing completed/total/failed periods
 * - Color changes based on failed periods (warning/error states)
 */
// Format date as YYMMDD
const formatDateCompact = (dateStr: string): string => {
  const d = new Date(dateStr)
  const yy = String(d.getFullYear()).slice(2)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yy}${mm}${dd}`
}

export function JobProgressBar({
  progressPct,
  periodsCompleted,
  totalPeriods,
  periodsFailed,
  periodStart,
  periodEnd,
  className
}: JobProgressBarProps) {
  // Determine progress bar color based on failures
  const getProgressColor = () => {
    if (periodsFailed > 0) {
      return 'bg-yellow-500' // Warning: has failures
    }
    return 'bg-blue-500' // Normal: no failures
  }

  const getTextColor = () => {
    if (periodsFailed > 0) {
      return 'text-yellow-700 dark:text-yellow-300'
    }
    return 'text-muted-foreground'
  }

  // Format date range if available (YYMMDD-YYMMDD)
  const dateRange = periodStart && periodEnd
    ? `${formatDateCompact(periodEnd)}-${formatDateCompact(periodStart)}`
    : null

  return (
    <div className={`space-y-1 ${className || ''}`}>
      <div className="flex items-center gap-2 text-xs flex-wrap">
        {dateRange ? (
          <span className={`font-mono ${getTextColor()} whitespace-nowrap`}>
            {dateRange}
          </span>
        ) : (
          <span className={`font-medium ${getTextColor()} whitespace-nowrap`}>
            {progressPct.toFixed(1)}%
          </span>
        )}
        <span className={`${getTextColor()} whitespace-nowrap`}>
          {periodsCompleted}/{totalPeriods}
        </span>
        {periodsFailed > 0 && (
          <span className="text-red-600 dark:text-red-400 whitespace-nowrap">
            ({periodsFailed} failed)
          </span>
        )}
      </div>
      <Progress
        value={progressPct}
        className="h-2"
        indicatorClassName={getProgressColor()}
      />
    </div>
  )
}
